import { FinancialTool } from "../../types";

export const fireAndRetirementNewTools: FinancialTool[] = [
  {
    id: "coast-fire",
    name: "Coast FIRE Calculator",
    description: "Find out if your current nest egg is large enough to coast into traditional retirement without saving another penny.",
    category: "retirement",
    inputs: [
      { id: "currentAge", label: "Current Age", type: "number", defaultValue: 28, min: 18, max: 79, step: 1 },
      { id: "retirementAge", label: "Target Retirement Age", type: "number", defaultValue: 60, min: 40, max: 80, step: 1 },
      { id: "currentSavings", label: "Current Portfolio Balance ($)", type: "currency", defaultValue: 85000, min: 0, max: 5000000, step: 5000 },
      { id: "annualExpenses", label: "Annual Expenses in Retirement ($)", type: "currency", defaultValue: 45000, min: 5000, max: 500000, step: 1000 },
      { id: "expectedReturn", label: "Expected Portfolio Return (%)", type: "percentage", defaultValue: 8.0, min: 1, max: 15, step: 0.1 },
      { id: "inflationRate", label: "Expected Inflation Rate (%)", type: "percentage", defaultValue: 2.5, min: 0, max: 10, step: 0.1 },
      { id: "swr", label: "Safe Withdrawal Rate (%)", type: "percentage", defaultValue: 4.0, min: 2.0, max: 6.0, step: 0.1 }
    ],
    calculate: (inputs) => {
      const currentAge = inputs.currentAge || 28;
      const retirementAge = inputs.retirementAge || 60;
      const currentSavings = inputs.currentSavings || 0;
      const annualExpenses = inputs.annualExpenses || 45000;
      const expectedReturn = inputs.expectedReturn || 8.0;
      const inflationRate = inputs.inflationRate || 2.5;
      const swr = (inputs.swr || 4) / 100;

      const realReturn = (expectedReturn - inflationRate) / 100;
      const yearsToCoast = Math.max(0, retirementAge - currentAge);
      
      const targetNestEgg = annualExpenses / swr;
      const coastFireNumber = targetNestEgg / Math.pow(1 + realReturn, yearsToCoast);
      
      const shortfall = Math.max(0, coastFireNumber - currentSavings);
      const isCoastFire = currentSavings >= coastFireNumber;

      const recommendation = isCoastFire 
        ? "Congratulations! You have achieved Coast FIRE. Your current nest egg is projected to reach your fully funded retirement goal on its own without further contributions."
        : `You need to invest an additional $${shortfall.toLocaleString("en-US", { maximumFractionDigits: 0 })} today, or save and compound further, to cross your immediate Coast FIRE threshold for age ${retirementAge}.`;

      return {
        outputs: [
          { label: "Your Coast FIRE Threshold Today", value: coastFireNumber, type: "currency", highlighted: true },
          { label: "Your Current Portofolio Balance", value: currentSavings, type: "currency" },
          { label: "Estimated Full Nest Egg at age " + retirementAge, value: currentSavings * Math.pow(1 + realReturn, yearsToCoast), type: "currency" },
          { label: "Required Nest Egg to Fund " + (swr * 100) + "% SWR", value: targetNestEgg, type: "currency", highlighted: true }
        ],
        chartData: [
          { name: "Your Assets Today", value: currentSavings },
          { name: "Coast FIRE Target Today", value: coastFireNumber },
          { name: "Full FIRE Nest Egg", value: targetNestEgg }
        ],
        recommendation
      };
    },
    guide: {
      overview: "Determine if your current stock and retirement portfolio can grow on its own without any further monthly deposits to fully support you when you retire.",
      howToUse: "Enter your current age, target retirement age, present investments, expected post-career expenses, investment return, inflation rate, and preferred safe withdrawal rate (SWR% Coefficient).",
      formulaExplanation: "Calculates the target retirement nest egg using SWR rules, and then discounts that amount back to the present day using inflation-adjusted (real) compound rates over your remaining career horizon: Coast FIRE = Target Portfolio / (1 + (Return - Inflation))^Years.",
      faq: [
        { question: "What is Coast FIRE?", answer: "Coast FIRE is a milestone within the FIRE movement where you have already accumulated enough assets. Even if you never contribute another dollar, they will compound over time to cover traditional retirement expenses by your target age." },
        { question: "Can I stop working once I reach Coast FIRE?", answer: "You cannot stop working entirely because you still need to cover your active seasonal living expenses day-to-day. However, you can switch to a lower-stress, more fulfilling job since you no longer need to save for retirement." }
      ],
      seoContent800Words: `The Coast FIRE concept is a highly liberating financial milestone. Unlike traditional financial independence which requires completely funding all life expenses via capital withdraws, **Coast FIRE** represents reaching an asset value today that, without any further contributions, will compound on its own to fund your retirement completely once you reach your target age. This changes the retirement planning timeline from decades of high-stress saving to a series of more flexible career options.

### The Mathematics Behind Coast FIRE Calculations

To determine if you have crossed this threshold, we must calculate what size retirement target portfolio you need, and then discount that number back to the present day using inflation-adjusted compound growth rates.
The core formula is:
$$PV_{\\text{Coast}} = \\frac{FV_{\\text{Target}}}{(1 + R_{\\text{real}})^t}$$
Where:
* **$FV_{\\text{Target}}$** is your required Nest Egg of retirement (Calculated as: Annual Expenses / Safe Withdrawal Rate).
* **$R_{\\text{real}}$** is the expected annual real return (Annual return minus estimated inflation rate).
* **$t$** is the remaining compounding horizon (Target retirement age minus current age).

If you are 28 years old, planning to retire at age 60, and estimate annual expenses of $45,000 under a standard 4.0% safe withdrawal rate:
1. Your target nest egg at 60 is:
   $$FV = \\frac{\\$45,000}{0.04} = \\$1,125,000$$
2. Assuming an 8.0% annual interest rate and a 2.5% inflation rate, your expected real return rate is **5.5%**.
3. Compounding over a **32-year** horizon:
   $$PV = \\frac{\\$1,125,000}{(1 + 0.055)^{32}} = \\$204,064.91$$

This means if you already have **$204,064.91** saved today at age 28, you can completely stop contributing to your 401(k) or IRA accounts. Your savings will coast to over $1,125,000 in real purchasing power completely on their own while you use all future earnings to cover your active lifestyle stress-free.

### Practical Career Applications of Reaching Coast FIRE

Reaching this milestone can allow you to make several dynamic changes to your career:
* **Take a Lower Paying, Lower Stress Job**: You can exit a grueling high-demand career and take a more creative or slow-paced job, since your only financial obligation is covering daily living costs.
* **Work Part-Time or Term**: You can work part-time or seasonally, freeing up substantial months of the year for travel, hobbies, or spending time with family.
* **Pursue Entrepreneurship**: Reaching Coast FIRE provides a reliable financial buffer to launch a passion business without the stress or pressure of needing immediate profits to fund your retirement.`
    }
  },
  {
    id: "barista-fire",
    name: "Barista FIRE Calculator",
    description: "Determine how much you need to save to semi-retire and let a part-time job cover your remaining daily expenses.",
    category: "retirement",
    inputs: [
      { id: "annualExpenses", label: "Target Annual Expenses ($)", type: "currency", defaultValue: 55000, min: 10000, max: 500000, step: 1000 },
      { id: "baristaIncome", label: "Part-Time Job Net Income ($/Yr)", type: "currency", defaultValue: 22000, min: 0, max: 200000, step: 1000 },
      { id: "currentSavings", label: "Current Portfolio Balance ($)", type: "currency", defaultValue: 150000, min: 0, max: 10000000, step: 5000 },
      { id: "swr", label: "Safe Withdrawal Rate (%)", type: "percentage", defaultValue: 4.0, min: 2.0, max: 6.0, step: 0.1 },
      { id: "savingsGrowth", label: "Expected Portfolio Real Growth (%)", type: "percentage", defaultValue: 6.0, min: 1, max: 15, step: 0.1 },
      { id: "monthlyContributions", label: "Monthly Contributions during build ($)", type: "currency", defaultValue: 1200, min: 0, max: 10000, step: 100 }
    ],
    calculate: (inputs) => {
      const annualExpenses = inputs.annualExpenses || 55000;
      const baristaIncome = inputs.baristaIncome || 0;
      const currentSavings = inputs.currentSavings || 0;
      const swr = (inputs.swr || 4) / 100;
      const growth = (inputs.savingsGrowth || 6) / 100 / 12;
      const mContrib = inputs.monthlyContributions || 0;

      const portfolioNeed = Math.max(0, annualExpenses - baristaIncome);
      const baristaFireTarget = swr > 0 ? portfolioNeed / swr : 0;
      const fullFireTarget = swr > 0 ? annualExpenses / swr : 0;
      const shortfall = Math.max(0, baristaFireTarget - currentSavings);

      let yearsToGoal = 0;
      let balance = currentSavings;
      
      if (balance < baristaFireTarget && mContrib > 0) {
        let months = 0;
        while (balance < baristaFireTarget && months < 600) {
          balance = (balance + mContrib) * (1 + growth);
          months++;
        }
        yearsToGoal = months / 12;
      }

      const rec = currentSavings >= baristaFireTarget
        ? "Excellent! You have achieved Barista FIRE. Your current portfolio can safely generate the remaining income needed when combined with your part-time wages."
        : `You have a shortfall of $${shortfall.toLocaleString("en-US", { maximumFractionDigits: 0 })} to reach your Barista FIRE target. Adding $${mContrib.toLocaleString("en-US")} monthly to your investments will get you there in approximately ${yearsToGoal.toFixed(1)} years.`;

      return {
        outputs: [
          { label: "Your Barista FIRE Portfolio Target", value: baristaFireTarget, type: "currency", highlighted: true },
          { label: "Annual Income Needed from Portfolio", value: portfolioNeed, type: "currency" },
          { label: "Barista Job Contribution (Wages)", value: baristaIncome, type: "currency" },
          { label: "Current Portfolio Shortfall", value: shortfall, type: "currency" },
          { label: "Years to Target Nest Egg", value: currentSavings >= baristaFireTarget ? 0 : (mContrib > 0 ? parseFloat(yearsToGoal.toFixed(1)) : "Never"), type: "number", highlighted: true },
          { label: "Alternative Total FIRE Target", value: fullFireTarget, type: "currency" }
        ],
        chartData: [
          { name: "Current Assets", value: currentSavings },
          { name: "Barista FIRE Target", value: baristaFireTarget },
          { name: "Full Independence Target", value: fullFireTarget }
        ],
        recommendation: rec
      };
    },
    guide: {
      overview: "Calculate your Barista FIRE milestone—where you retire early, but continue working a low-stress, enjoyable part-time job to offset a portion of your living costs.",
      howToUse: "Enter your overall target annual expenses, part-time wages, current investment balance, SWR%, expected real portfolio return rate, and ongoing savings rate.",
      formulaExplanation: "Calculates the expenses covered by your portfolio (Annual Expenses minus Part-Time Wages) and divides this remainder by your Safe Withdrawal Rate to reveal your custom Barista FIRE Target Nest Egg.",
      faq: [
        { question: "What is Barista FIRE?", answer: "Barista FIRE is a popular strategy where you accumulate enough investments to cover some of your costs, while switching to a flexible part-time or low-stress job (like a barista) to cover the rest and secure group health insurance." },
        { question: "How does Barista FIRE compare to Full FIRE?", answer: "Full FIRE requires saving 25 times your total annual expenses to fund your life without any active work. Barista FIRE requires a much smaller retirement target because part-time wages cover a portion of your ongoing budget." }
      ],
      seoContent800Words: `The **Barista FIRE** path represents a highly practical compromise between traditional early retirement and a standard career path. Many workers find that they do not want to stop working entirely, but rather want to transition away from high-stress corporate environments. By planning to work a flexible part-time job, you can significantly reduce your required nest egg target and accelerate your early retirement timeline.

### Real-World Mathematics of Barista FIRE

Your Barista FIRE timeline is driven by your annual expenses, part-time income, and safe withdrawal rate. 
The core formula to find your required portfolio is:
$$\\text{Barista FIRE Target} = \\frac{\\text{Annual Expenses} - \\text{Part-Time Wages}}{\\text{withdrawal Rate}}$$

For example, if you require a target lifestyle of $55,000 annually, but plan to spend your time working a creative part-time job that pays a secure, predictable $22,000 net salary:
1. Your portfolio is only needed to cover the annual remainder of:
   $$\\text{Portfolio Gap} = \\$55,000 - \\$22,000 = \\$33,000 \\text{ per year}$$
2. Under a standard 4.0% safe withdrawal rate (SWR), your required Barista FIRE portfolio is:
   $$\\text{Nest Egg} = \\frac{\\$33,000}{0.04} = \\$825,000$$
3. By comparison, achieving **Full Traditional FIRE** requires building a nest egg of:
   $$\\text{Full Nest Egg} = \\frac{\\$55,000}{0.04} = \\$1,375,000$$

This reduces your target by **$550,000**, potentially shaving 5 to 10 years off your high-stress working window. This tool incorporates ongoing contributions to map exactly when you can transition to this comfortable lifestyle.

### Essential Benefits Beyond Active Wages

* **Guaranteed Health Insurance Integration**: In countries like the United States, medical insurance represents a major expense for early retirees. Many Barista FIRE practitioners work part-time at companies that offer excellent health insurance benefits to part-time workers (such as Costco, Target, or Starbucks), saving thousands in premium costs.
* **Social and Structure Outlets**: Work provides structure, routine, and rich human interaction. Part-time roles allow you to enjoy these social rewards without the high-stress demands and office politics of corporate jobs.
* **Wealth Protection Buffer**: During stock market downturns, your part-time income remains stable. This prevents you from needing to sell depressed stocks to cover expenses, protecting your portfolio's longevity.`
    }
  },
  {
    id: "lean-fire",
    name: "Lean FIRE Calculator",
    description: "Plan for early retirement based on a highly optimized, minimalist cost of living.",
    category: "retirement",
    inputs: [
      { id: "annualExpenses", label: "Minimalist Annual Expenses ($)", type: "currency", defaultValue: 32000, min: 10000, max: 70000, step: 1000 },
      { id: "currentSavings", label: "Current Assets Balance ($)", type: "currency", defaultValue: 120000, min: 0, max: 3000000, step: 5000 },
      { id: "monthlySavings", label: "Monthly Savings Rate ($)", type: "currency", defaultValue: 1500, min: 0, max: 15000, step: 100 },
      { id: "swr", label: "Safe Withdrawal Rate (%)", type: "percentage", defaultValue: 3.5, min: 2.0, max: 5.0, step: 0.1 },
      { id: "annualReturn", label: "Portfolio Growth (Real) (%)", type: "percentage", defaultValue: 6.5, min: 1, max: 15, step: 0.1 }
    ],
    calculate: (inputs) => {
      const expenses = inputs.annualExpenses || 32000;
      const current = inputs.currentSavings || 0;
      const savings = inputs.monthlySavings || 0;
      const swr = (inputs.swr || 3.5) / 100;
      const rate = (inputs.annualReturn || 6.5) / 100 / 12;

      const leanTarget = expenses / swr;
      const shortfall = Math.max(0, leanTarget - current);

      let years = 0;
      let balance = current;
      if (balance < leanTarget && savings > 0) {
        let months = 0;
        while (balance < leanTarget && months < 600) {
          balance = (balance + savings) * (1 + rate);
          months++;
        }
        years = months / 12;
      }

      const rec = current >= leanTarget
        ? "Success! You have achieved Lean FIRE. Your portfolio is large enough to fund a highly optimized, minimalist lifestyle."
        : `You need $${shortfall.toLocaleString("en-US", { maximumFractionDigits: 0 })} more to hit Lean FIRE. With your current monthly contributions of $${savings.toLocaleString("en-US")}, you are estimated to reach this goal in ${years.toFixed(1)} years.`;

      return {
        outputs: [
          { label: "Lean FIRE Portfolio Target", value: leanTarget, type: "currency", highlighted: true },
          { label: "Current Portfolio Balance", value: current, type: "currency" },
          { label: "Your Nest Egg Shortfall", value: shortfall, type: "currency" },
          { label: "Years to Lean FIRE Target", value: current >= leanTarget ? 0 : (savings > 0 ? parseFloat(years.toFixed(1)) : "Never"), type: "number", highlighted: true },
          { label: "Monthly Inflation-Adjusted Income", value: (leanTarget * swr) / 12, type: "currency" }
        ],
        chartData: [
          { name: "My Savings Today", value: current },
          { name: "Lean FIRE Goal", value: leanTarget }
        ],
        recommendation: rec
      };
    },
    guide: {
      overview: "Plan your path to a minimalist, low-cost early retirement using Lean FIRE guidelines, which focus on keeping annual expenses under $40,000.",
      howToUse: "Enter your expected minimalist living expenses, current asset base, ongoing monthly savings, chosen safe withdrawal rate, and projected portfolio real return.",
      formulaExplanation: "Lean FIRE divides your expected minimalist annual expenses by your safe withdrawal rate to calculate your required early retirement portfolio size.",
      faq: [
        { question: "What is Lean FIRE?", answer: "Lean FIRE represents achieving financial independence on a lean, optimized budget—typically defined as having annual expenses under $40,000. It requires a smaller portfolio size and a faster accumulation timeline than traditional retirement." },
        { question: "Is Lean FIRE safe in retirement?", answer: "Yes, provided you choose a slightly more conservative Safe Withdrawal Rate (such as 3.5%) to account for your smaller expense buffer, and maintain a flexible budget." }
      ],
      seoContent800Words: `The **Lean FIRE** movement is a popular strategy for those who prioritize time and freedom over material goods. By maintaining a highly optimized, low-overhead lifestyle, you can escape the standard corporate career path much faster than traditional savers. 

### Decoding your Lean FIRE Target Portfolio

Reaching Lean FIRE typically means designing an lifestyle with annual expenses below $40,000. Since your expenses are highly optimized, you require a much smaller portfolio to be financially independent.
The formula to find your target portfolio size is:
$$\\text{Lean FIRE Portfolio} = \\frac{\\text{Minimalist Expenses}}{\\text{Safe Withdrawal Rate}}$$

For example, if you live an elegant, minimalist lifestyle that costs $32,000 a year:
1. Under a safe withdrawal rate of 3.5% (designed to protect your portfolio over a long 40-year horizon):
   $$\\text{Target Portfolio} = \\frac{\\$32,000}{0.035} = \\$914,285.71$$
2. Under a standard 4.0% withdrawal rate:
   $$\\text{Target Portfolio} = \\frac{\\$32,000}{0.04} = \\$800,000$$

With a starting capital of $120,000 and depositing $1,500 monthly at a conservative 6.5% interest rate, you will reach your **$914,285.71** target in roughly **18 years**. This tool incorporates your exact investment parameters to map out your timeline.

### Strategic Optimization to Speed Up Your Lean FIRE Timeline

* **Select Low-Cost-of-Living Cities**: Geographically optimizing your residence (geographic arbitrage) by moving to lower-tax states or affordable international destinations instantly slashes your housing, transportation, and healthcare costs.
* **Minimize High-Cost Consumer Subscriptions**: Audit recurring monthly payments, stream services, and memberships, directing all newly optimized cash directly into your investment account.
* **Maintain High-Quality Flexibilities**: Since your budget is lean, preserve a small discretionary buffer where you can reduce expenses or take on minor seasonal side-gigs during stock market downturns to protect your portfolio from sequence of returns risk.`
    }
  },
  {
    id: "fat-fire",
    name: "Fat FIRE Calculator",
    description: "Plan for a wealthy, high-spend early retirement without compromises on lifestyle.",
    category: "retirement",
    inputs: [
      { id: "annualExpenses", label: "Luxury Annual Expenses ($)", type: "currency", defaultValue: 140000, min: 80000, max: 1000000, step: 5000 },
      { id: "currentSavings", label: "Current Assets Balance ($)", type: "currency", defaultValue: 450000, min: 0, max: 15000000, step: 10000 },
      { id: "monthlySavings", label: "Monthly Savings Rate ($)", type: "currency", defaultValue: 3500, min: 0, max: 50000, step: 250 },
      { id: "swr", label: "Safe Withdrawal Rate (%)", type: "percentage", defaultValue: 3.5, min: 2.0, max: 5.0, step: 0.1 },
      { id: "annualReturn", label: "Portfolio Growth (Real) (%)", type: "percentage", defaultValue: 6.5, min: 1, max: 15, step: 0.1 }
    ],
    calculate: (inputs) => {
      const expenses = inputs.annualExpenses || 140000;
      const current = inputs.currentSavings || 0;
      const savings = inputs.monthlySavings || 0;
      const swr = (inputs.swr || 3.5) / 100;
      const rate = (inputs.annualReturn || 6.5) / 100 / 12;

      const fatTarget = expenses / swr;
      const shortfall = Math.max(0, fatTarget - current);

      let years = 0;
      let balance = current;
      if (balance < fatTarget && savings > 0) {
        let months = 0;
        while (balance < fatTarget && months < 600) {
          balance = (balance + savings) * (1 + rate);
          months++;
        }
        years = months / 12;
      }

      const rec = current >= fatTarget
        ? "Amazing! You have achieved Fat FIRE. Your portfolio can fully support a wealthy, high-spending early retirement."
        : `You need $${shortfall.toLocaleString("en-US", { maximumFractionDigits: 0 })} more to achieve Fat FIRE. Contributing $${savings.toLocaleString("en-US")} monthly is projected to get you there in ${years.toFixed(1)} years.`;

      return {
        outputs: [
          { label: "Fat FIRE Portfolio Target", value: fatTarget, type: "currency", highlighted: true },
          { label: "Current Portfolio Balance", value: current, type: "currency" },
          { label: "Your Nest Egg Shortfall", value: shortfall, type: "currency" },
          { label: "Years to Fat FIRE Target", value: current >= fatTarget ? 0 : (savings > 0 ? parseFloat(years.toFixed(1)) : "Never"), type: "number", highlighted: true },
          { label: "Monthly High-Spend Income Produced", value: (fatTarget * swr) / 12, type: "currency" }
        ],
        chartData: [
          { name: "My Savings Today", value: current },
          { name: "Fat FIRE Goal", value: fatTarget }
        ],
        recommendation: rec
      };
    },
    guide: {
      overview: "Plan for a luxurious early retirement using Fat FIRE guidelines, which focus on securing annual budgets over $100,000.",
      howToUse: "Enter your target luxury budget, current investments, ongoing monthly savings, chosen safe withdrawal rate, and estimated real return rate.",
      formulaExplanation: "Fat FIRE divides your target luxury annual expenses by your safe withdrawal rate to calculate your required luxury portfolio size.",
      faq: [
        { question: "What is Fat FIRE?", answer: "Fat FIRE represents achieving financial independence with a luxurious lifestyle—typically defined as having annual expenses over $100,000. It requires building a much larger portfolio, allowing you to retire early without needing to compromise on travel, dining, or hobbies." },
        { question: "Why is a lower SWR recommended for Fat FIRE?", answer: "Because your budget is high, building in a safer, lower Safe Withdrawal Rate (such as 3.25% or 3.5%) ensures your portfolio can easily withstand prolonged market downturns without needing you to slash your spending." }
      ],
      seoContent800Words: `The **Fat FIRE** movement is designed for individuals who want to retire early but maintain a premium, high-spend lifestyle. Rather than embracing minimalism or cutting costs, Fat FIRE practitioners build larger portfolios to fund extensive travel, comfortable homes, hobbies, and peace of mind.

### The Mathematics Behind Fat FIRE Targets

To support a luxurious early retirement, your annual spending typically starts at $100,000 to $150,000 or more. Because your spending is high, early retirement planners suggest using a slightly more conservative Safe Withdrawal Rate of 3.25% to 3.5% to protect your nest egg.
The formula is:
$$\\text{Fat FIRE Nest Egg} = \\frac{\\text{Luxury Expenses}}{\\text{Safe Withdrawal Rate}}$$

For example, if you plan for a luxury retirement budget of $140,000 annually:
1. Under a conservative 3.5% safe withdrawal rate:
   $$\\text{Portfolio Needed} = \\frac{\\$140,000}{0.035} = \\$4,000,000$$
2. Under a standard 4.0% withdrawal rate:
   $$\\text{Portfolio Needed} = \\frac{\\$140,000}{0.04} = \\$3,500,000$$

While building a $4,000,000 portfolio requires considerable time, starting with $450,000 and investing $3,500 a month with an expected 6.5% real return will get you there in approximately **22.5 years**. This tool tracks your exact savings rates to help you visualize your progress.

### Core Lifestyle Ratios of Fat FIRE Retirees

* **Built-in Healthcare and Insurance Protection**: A major slice of high-level retirement budgets goes to private health insurance. Fat FIRE builds these high premiums directly into the plan, ensuring you have premium medical coverage.
* **Extensive Global Travel Buffers**: High inflation-adjusted budgets allow you to vacation in comfort, buy premium flights, and lease temporary winter/summer homes across the globe.
* **Significant Multi-Generational Wealth Creation**: Accumulating a large, robust portfolio increases the likelihood that your assets will continue to grow, leaving a substantial financial legacy for your family or chosen charities.`
    }
  },
  {
    id: "fire-withdrawal",
    name: "FIRE Withdrawal Simulator",
    description: "Simulate how your portfolio will survive over various retirement horizons under different annual post-career withdrawal rules.",
    category: "retirement",
    inputs: [
      { id: "nestEgg", label: "Initial Portfolio Balance ($)", type: "currency", defaultValue: 1200000, min: 10000, max: 15000000, step: 10000 },
      { id: "annualWithdrawal", label: "Initial Annual Withdrawal ($)", type: "currency", defaultValue: 48000, min: 5000, max: 1000000, step: 1000 },
      { id: "expectedReturn", label: "Annual Investment Return (%)", type: "percentage", defaultValue: 7.0, min: 1, max: 15, step: 0.1 },
      { id: "inflationRate", label: "Yearly Inflation Rate (%)", type: "percentage", defaultValue: 2.5, min: 0, max: 10, step: 0.1 },
      { id: "years", label: "Horizon Length (Years)", type: "number", defaultValue: 35, min: 10, max: 60, step: 1 }
    ],
    calculate: (inputs) => {
      const nestEgg = inputs.nestEgg || 1200000;
      const annualWithdrawal = inputs.annualWithdrawal || 48000;
      const returnRate = inputs.expectedReturn || 7.0;
      const inflation = inputs.inflationRate || 2.5;
      const years = inputs.years || 35;

      let balance = nestEgg;
      let withdrawal = annualWithdrawal;
      let completed = true;
      let depletedYear = 0;
      let totalWithdrawn = 0;
      
      const chartPoints = [];
      const interval = Math.max(1, Math.round(years / 8));

      for (let y = 1; y <= years; y++) {
        // Safe check
        if (balance >= withdrawal) {
          balance = balance - withdrawal;
          totalWithdrawn += withdrawal;
          balance = balance * (1 + (returnRate / 100));
        } else {
          totalWithdrawn += balance;
          balance = 0;
          if (completed) {
            completed = false;
            depletedYear = y;
          }
        }
        
        withdrawal = withdrawal * (1 + (inflation / 100));

        if (y === 1 || y === years || y % interval === 0) {
          chartPoints.push({ name: `Year ${y}`, value: Math.round(balance) });
        }
      }

      const realValueEnd = balance / Math.pow(1 + (inflation / 100), years);

      const rec = completed
        ? `Congratulations! Your portfolio successfully survived your ${years}-year retirement horizon under this withdrawal rate, leaving an inflation-adjusted balance of $${realValueEnd.toLocaleString("en-US", { maximumFractionDigits: 0 })} in real purchasing power.`
        : `Caution: Your portfolio was depleted in Year ${depletedYear}. Consider lowering your initial withdrawal to avoid running out of money.`;

      return {
        outputs: [
          { label: "Ending Balance (Nominal)", value: balance, type: "currency", highlighted: true },
          { label: "Survival Diagnosis", value: completed ? "Fully Survived" : `Depleted in Year ${depletedYear}`, type: "text", highlighted: !completed },
          { label: "Total Inflation-Adjusted Withdrawn", value: totalWithdrawn, type: "currency" },
          { label: "Ending Balance (Real Purchasing Power)", value: realValueEnd, type: "currency" }
        ],
        chartData: chartPoints,
        recommendation: rec
      };
    },
    guide: {
      overview: "Determine if your retirement nest egg can survive your target horizon using yearly inflation-adjusted cash drawdowns.",
      howToUse: "Enter your starting portfolio balance, your initial annual withdrawal amount, expected portfolio returns, projected inflation, and horizon length.",
      formulaExplanation: "Calculates the year-by-year balance of your portfolio by reducing it by your annual withdrawal first, compounding the remaining balance, and indexing your next withdrawal for inflation: Balance_New = (Balance_Prev - Withdrawal_Prev) * (1 + Return).",
      faq: [
        { question: "Is inflation-adjusting my withdrawals necessary?", answer: "Yes. An annual withdrawal of $40,000 will buy far fewer groceries and services in 20 years. Indexing your withdrawal to inflation ensures your purchasing power remains stable throughout retirement." },
        { question: "What represents a safe retirement return rate?", answer: "Many planners use a conservative real return rate (portfolio gains minus inflation) of 4.5% to 6.5% for diversified global asset portfolios." }
      ],
      seoContent800Words: `When planning for early retirement, understanding how your assets handle withdrawals over time is critical. The **FIRE Withdrawal Simulator** allows you to test different annual spending budgets, return rates, and inflation levels to project your portfolio's longevity.

### Traditional Mathematical Modality of SWR Projections

Our tool runs a year-by-year compounding simulation using your custom parameters. The calculation sequence is:
$$\\text{Withdrawal}_t = \\text{Withdrawal}_{t-1} \\times (1 + \\text{Inflation Rate})$$
$$\\text{Balance}_t = (\\text{Balance}_{t-1} - \\text{Withdrawal}_t) \\times (1 + \\text{Return Rate})$$

For example, on an initial portfolio of $1,200,000 under an initial annual withdrawal of $48,000 (a standard 4.0% withdrawal rate) with a 7.0% return rate and a 2.5% inflation rate:
* In Year 1: You withdraw **$48,000** immediately, leaving $1,152,000, which grows to **$1,232,640.00** by year-end.
* In Year 2: Your withdrawal is adjusted upward for 2.5% inflation to **$49,200**, leaving $1,183,440, which grows to **$1,266,280.80** by year-end.
* Over 35 years: This compounding sequence completely offsets your withdrawals, maintaining your portfolio's purchasing power and leaving an inflation-adjusted balance of **$1,170,412.00**.

Our simulator maps out these complex yearly interactions clearly so you can identify safe spending limits.

### Protecting Your Portfolio from Key Retirement Risks

* **Sequence of Returns Risk**: Experiencing stock market downturns in your first few years of retirement can significantly impact your portfolio's longevity. Building a cash buffer or reducing withdrawals during market declines can help mitigate this risk.
* **Prolonged Inflation Pressures**: High inflation erodes your purchasing power quickly, forcing you to make larger withdrawals. Investing in assets that historically outpace inflation (such as global stocks and real estate) is essential.
* **Underestimating Horizonal Longevity**: With early retirement, your portfolio may need to support you for 40 to 50 years. Choosing a more conservative safe withdrawal rate protects your assets for the long term.`
    }
  },
  {
    id: "swr-calculator",
    name: "Safe Withdrawal Rate Calculator",
    description: "Determine your portfolio's sustainable withdrawal rate against different safety thresholds and economic periods.",
    category: "retirement",
    inputs: [
      { id: "portfolioValue", label: "Initial Portfolio Value ($)", type: "currency", defaultValue: 1000000, min: 10000, max: 10000000, step: 10000 },
      { id: "withdrawalRate", label: "Proposed Withdrawal Rate (%)", type: "percentage", defaultValue: 4.0, min: 1.0, max: 10.0, step: 0.1 },
      { id: "years", label: "Age Durational Horizon (Years)", type: "number", defaultValue: 30, min: 10, max: 60, step: 1 },
      { id: "returnRate", label: "Portfolio Expected Return (%)", type: "percentage", defaultValue: 7.5, min: 1, max: 15, step: 0.1 },
      { id: "inflationRate", label: "Expected Inflation Rate (%)", type: "percentage", defaultValue: 2.5, min: 0, max: 10, step: 0.1 }
    ],
    calculate: (inputs) => {
      const value = inputs.portfolioValue || 1000000;
      const pct = inputs.withdrawalRate || 4.0;
      const years = inputs.years || 30;
      const returnRate = inputs.returnRate || 7.5;
      const inflation = inputs.inflationRate || 2.5;

      const annualIncome = value * (pct / 100);

      const runSim = (ret: number, inf: number) => {
        let bal = value;
        let pOut = annualIncome;
        for (let i = 1; i <= years; i++) {
          bal = bal - pOut;
          if (bal < 0) return i - 1;
          bal = bal * (1 + (ret / 100));
          pOut = pOut * (1 + (inf / 100));
        }
        return years;
      };

      const survivalOptimistic = runSim(9.0, 2.0);
      const survivalModerate = runSim(returnRate, inflation);
      const survivalConservative = runSim(5.0, 3.0);

      const getRecommendedSWR = (y: number) => {
        if (y >= 45) return "3.0% to 3.25%";
        if (y >= 30) return "3.5% to 4.0%";
        if (y >= 20) return "4.25% to 4.75%";
        return "5.0% to 6.0%";
      };

      const diagnosis = survivalModerate >= years
        ? `Sustenance Confirmed. Your portfolio survived the Moderate regime after ${years} years.`
        : `Risk Identified: Depleted in Year ${survivalModerate} under Moderate conditions.`;

      return {
        outputs: [
          { label: "Proposed Annual Income", value: annualIncome, type: "currency", highlighted: true },
          { label: "Moderate Scenario Longevity", value: survivalModerate === years ? `Fully Survived (${years}+ Yrs)` : `${survivalModerate} Years`, type: "text" },
          { label: "Conservative Scenario Longevity", value: survivalConservative === years ? `Fully Survived (${years}+ Yrs)` : `${survivalConservative} Years`, type: "text" },
          { label: "Recommended SWR for " + years + "-Yr Horizon", value: getRecommendedSWR(years), type: "text", highlighted: true }
        ],
        chartData: [
          { name: "Conservative", value: survivalConservative },
          { name: "Moderate (Your Rate)", value: survivalModerate },
          { name: "Optimistic", value: survivalOptimistic }
        ],
        recommendation: diagnosis
      };
    },
    guide: {
      overview: "Determine a safe, sustainable spending rate for your investments that protects you from running out of money, across various market conditions.",
      howToUse: "Enter your initial portfolio balance, proposed withdrawal rate, retirement horizon, expected growth rate, and inflation.",
      formulaExplanation: "Calculates your initial annual income and uses historical safety guardrails to simulate portfolio survival across three distinct compounding regimes: Conservative (5% return), Moderate (your custom return), and Optimistic (9% return).",
      faq: [
        { question: "What is the famous 4% rule?", answer: "The 4% rule is a guideline based on the Trinity Study. It states that you can safely withdraw 4% of your portfolio in your first retirement year, adjust that amount for inflation annually, and face a negligible risk of runs out of money over 30 years." },
        { question: "Should early retirees use a lower SWR?", answer: "Yes. Early retirement horizons often extend to 40 or 50 years. Planners suggest using a safer rate of 3.25% to 3.5% to protect your portfolio over these longer timelines." }
      ],
      seoContent800Words: `Selecting a sustainable withdrawal rate is one of the most critical decisions for early retirees. Your **Safe Withdrawal Rate (SWR)** balances your desire to enjoy your wealth with the need to protect your portfolio from market downturns.

### Analyzing the Trinity Study and historical Rates

The classic 4.0% safe withdrawal guideline is based on the Trinity Study, which analyzed retirement portfolios across 30-year horizons.
The calculation is:
$$\\text{First Year Payout} = \\text{Portfolio Value} \\times \\text{SWR}\\%$$

For a $1,000,000 portfolio with a 4.0% SWR:
1. Your first-year payout is:
   $$\\text{Payout} = \\$1,000,000 \\times 0.04 = \\$40,000$$
2. In subsequent years, you increase your payout size to match inflation, regardless of market performance.
3. Over a 30-year horizon, this strategy succeeded in over 95% of historical market cycles.

However, early retirement horizons often extend to 40 or 50 years. For these longer timelines, planners suggest using a safer rate of **3.25% to 3.5%** to protect your portfolio. This tool runs simulations across three market regimes to help you select a safe withdrawal rate.

### Essential Methods to Dynamically Protect Your Portfolio

* **Flexible Spending Rules (Guardrails)**: Instead of strictly increasing your withdrawals for inflation, you can adjust your spending based on market performance—reducing withdrawals during down years to protect your portfolio.
* **The Cash Buffer Strategy**: Maintaining a 1 to 2-year cash or short-term bond reserve ensures you have reliable assets to draw from during market downturns, preventing you from needing to sell depressed stocks.
* **The Yield Shield Method**: Focus your portfolio on assets that generate steady dividend income or bond yields, allowing you to fund your lifestyle entirely from cash flow without needing to sell principal.`
    }
  },
  {
    id: "retirement-age",
    name: "Retirement Age Calculator",
    description: "Find out the exact year and age you can retire based on your earnings, spending, and portfolio growth rate.",
    category: "retirement",
    inputs: [
      { id: "currentAge", label: "Current Age", type: "number", defaultValue: 30, min: 18, max: 75, step: 1 },
      { id: "netIncome", label: "Annual Net Income ($)", type: "currency", defaultValue: 75000, min: 10000, max: 1000000, step: 1000 },
      { id: "annualSpending", label: "Annual Living Expenses ($)", type: "currency", defaultValue: 42000, min: 5000, max: 500000, step: 1000 },
      { id: "currentNetworth", label: "Current Investments ($)", type: "currency", defaultValue: 45000, min: 0, max: 10000000, step: 5000 },
      { id: "expectedReturn", label: "Portfolio Real Return (%)", type: "percentage", defaultValue: 6.0, min: 1, max: 15, step: 0.1 },
      { id: "swr", label: "Safe Withdrawal Rate (%)", type: "percentage", defaultValue: 4.0, min: 2.0, max: 6.0, step: 0.1 }
    ],
    calculate: (inputs) => {
      const currentAge = inputs.currentAge || 30;
      const netIncome = inputs.netIncome || 75000;
      const annualSpending = inputs.annualSpending || 42000;
      const currentNetworth = inputs.currentNetworth || 0;
      const expectedReturn = inputs.expectedReturn || 6.0;
      const swr = (inputs.swr || 4.0) / 100;

      const annualSavings = Math.max(0, netIncome - annualSpending);
      const targetNestEgg = annualSpending / swr;
      const r = expectedReturn / 100;

      let years = 0;
      let balance = currentNetworth;

      if (balance >= targetNestEgg) {
        years = 0;
      } else if (annualSavings <= 0) {
        years = 99; // Never
      } else {
        while (balance < targetNestEgg && years < 60) {
          balance = (balance + annualSavings) * (1 + r);
          years++;
        }
      }

      const savingsRate = netIncome > 0 ? (annualSavings / netIncome) * 100 : 0;
      const retirementAge = currentAge + Math.round(years);

      const recommendation = annualSavings <= 0
        ? "Caution: Your expenses match or exceed your income, leaving no savings to invest. Increase your savings rate to build towards retirement."
        : `Based on your current savings rate of ${savingsRate.toFixed(1)}%, you are projected to hit your early retirement goal in approximately ${Math.round(years)} years (at age ${retirementAge}).`;

      return {
        outputs: [
          { label: "Estimated Retirement Age", value: years === 99 ? "Never" : retirementAge, type: "text", highlighted: true },
          { label: "Your Target Nest Egg Needed", value: targetNestEgg, type: "currency", highlighted: true },
          { label: "Annual Savings Growth Payout", value: annualSavings, type: "currency" },
          { label: "Household Savings Rate Today", value: savingsRate, type: "percentage" },
          { label: "Years Until Financial Freedom", value: years === 99 ? "Never" : Math.round(years), type: "number" }
        ],
        chartData: [
          { name: "Current Assets", value: currentNetworth },
          { name: "Target Nest Egg Needed", value: targetNestEgg }
        ],
        recommendation
      };
    },
    guide: {
      overview: "Determine the exact timeline and age you can retire, based on your current savings rate and expected investment returns.",
      howToUse: "Enter your current age, take-home net income, annual living expenses, starting assets, real investment growth, and safe withdrawal rate (SWR).",
      formulaExplanation: "Calculates your annual savings rate, maps your target portfolio size from expenses, and solves for the compounding horizon needed to bridge the gap using standard real return projections.",
      faq: [
        { question: "Why is savings rate the most important factor?", answer: "Because your savings rate determines two things: how much money you save each year to invest, and how much money you actually spend. A higher savings rate naturally reduces your retirement target." },
        { question: "How does inflation impact my retirement age?", answer: "By using an expected return rate adjusted for inflation (real return rate), the calculator maps all parameters in today's dollars, keeping projections accurate." }
      ],
      seoContent800Words: `The **Retirement Age Calculator** is a valuable tool for planning your financial future. In personal finance, your actual retirement age is driven primarily by your savings rate rather than standard retirement age norms.

### The Mathematics Behind Savings Rates and Retirement Timelines

The time required to reach financial independence is determined by your savings rate—the percentage of your net income you save and invest:
$$\\text{Savings Rate} = \\frac{\\text{Net Income} - \\text{Expenses}}{\\text{Net Income}} \\times 100$$

Here is how your savings rate affects your working years (assuming a standard 7.0% real return and 4.0% SWR):
* **10% Savings Rate**: Requires approximately **51 years** of work to fund your retirement.
* **30% Savings Rate**: Requires approximately **28 years** of work to fund your retirement.
* **50% Savings Rate**: Requires approximately **17 years** of work to fund your retirement.
* **70% Savings Rate**: Requires approximately **8.5 years** of work to fund your retirement.

By increasing your savings rate, you simultaneously build your portfolio faster and reduce your lifestyle overhead, lowering your retirement target. This tool tracks these parameters to project your exact retirement timeline.

### Essential Methods to Safely Accelerate Your Retirement Age

1. **Avoid Lifestyle Inflation**: As your salary increases, resist the urge to inflate your spending. Directing all salary increases straight to your investments can shave years off your timeline.
2. **Optimize housing Costs**: Housing is typically your largest annual expense. Cutting housing costs by downsizing, house hacking, or moving to a more affordable area is highly effective.
3. **Focus on Tax Shelter Maximization**: Direct your savings to tax-deferred or tax-free accounts (such as 401(k) Traditional, Traditional IRA, or HSA) to shield your returns and compound your gains faster.`
    }
  },
  {
    id: "retirement-gap",
    name: "Retirement Gap Calculator",
    description: "Analyze the difference between your projected nest egg and what you actually need to retire comfortably.",
    category: "retirement",
    inputs: [
      { id: "desiredIncome", label: "Desired Annual Income ($)", type: "currency", defaultValue: 80000, min: 10000, max: 1000000, step: 2500 },
      { id: "socialSecurityPension", label: "Guaranteed Annual Non-Portfolio Income ($)", type: "currency", defaultValue: 25000, min: 0, max: 250000, step: 1000 },
      { id: "projectedNestEgg", label: "Projected Nest Egg at Retirement ($)", type: "currency", defaultValue: 900000, min: 0, max: 10000000, step: 10000 },
      { id: "swr", label: "Target Safe Withdrawal Rate (%)", type: "percentage", defaultValue: 4.0, min: 2.0, max: 6.0, step: 0.1 }
    ],
    calculate: (inputs) => {
      const desiredIncome = inputs.desiredIncome || 80000;
      const otherIncome = inputs.socialSecurityPension || 0;
      const nestEgg = inputs.projectedNestEgg || 0;
      const swr = (inputs.swr || 4.5) / 100;

      const portfolioIncomeNeeded = Math.max(0, desiredIncome - otherIncome);
      const requiredNestEgg = swr > 0 ? portfolioIncomeNeeded / swr : 0;
      
      const gap = Math.max(0, requiredNestEgg - nestEgg);
      const isMet = nestEgg >= requiredNestEgg;

      const rec = isMet
        ? "Excellent! You have successfully closed your retirement gap. Your projected nest egg is expected to fully fund your desired lifestyle."
        : `You have a retirement gap of $${gap.toLocaleString("en-US", { maximumFractionDigits: 0 })}. Consider increasing your contributions or delaying retirement to close this gap.`;

      return {
        outputs: [
          { label: "Target Nest Egg Needed", value: requiredNestEgg, type: "currency", highlighted: true },
          { label: "Projected Nest Egg Assets", value: nestEgg, type: "currency" },
          { label: "Retirement Shortfall / Gap", value: gap, type: "currency", highlighted: !isMet },
          { label: "Portfolio Income Gap (Annual)", value: Math.max(0, portfolioIncomeNeeded - (nestEgg * swr)), type: "currency" },
          { label: "Net Portfolio Income Produced", value: nestEgg * swr, type: "currency" }
        ],
        chartData: [
          { name: "Projected Nest Egg Assets", value: nestEgg },
          { name: "Remaining Gap Shortfall", value: gap }
        ],
        recommendation: rec
      };
    },
    guide: {
      overview: "Determine the exact gap between your projected retirement savings and what you need to fund your lifestyle, after accounting for non-portfolio income streams.",
      howToUse: "Enter your desired annual retirement income, other guaranteed income (such as Social Security or pension payouts), your projected nest egg, and your target safe withdrawal rate.",
      formulaExplanation: "Calculates your annual income gap, divides this gap by your safe withdrawal rate to establish your required nest egg, and subtracts your projected savings to reveal your retirement shortfall.",
      faq: [
        { question: "What is a retirement gap?", answer: "A retirement gap represents the shortfall between your projected retirement savings and what you actually need to support your spending. Closing this gap is critical to securing your retirement." },
        { question: "How does Social Security affect my retirement gap?", answer: "Social Security covers a portion of your living expenses, which directly reduces your portfolio income shortfall and lowers your required retirement target." }
      ],
      seoContent800Words: `Understanding and closing your **retirement gap** is essential to planning a secure financial future. This gap represents the shortfall between your projected retirement savings and what you need to fund your lifestyle, after accounting for other guaranteed income streams.

### Calculating Your Retirement Gap

To find your retirement gap, we first calculate your portfolio income shortfall—the gap between your desired retirement income and your guaranteed pension and Social Security payouts:
$$\\text{Portfolio Income Shortfall} = \\text{Desired Income} - \\text{Social Security/Pensions}$$
$$\\text{Required Nest Egg} = \\frac{\\text{Shortfall}}{\\text{withdrawal Rate}}$$
$$\\text{Retirement Gap} = \\text{Required Nest Egg} - \\text{Projected Savings}$$

For example, on a desired annual retirement lifestyle that costs $80,000, with $25,000 in combined Social Security benefits:
1. Your portfolio is needed to fund the remaining:
   $$\\text{Shortfall} = \\$80,000 - \\$25,000 = \\$55,000 \\text{ per year}$$
2. Under a standard 4.0% safe withdrawal rate (SWR), your required retirement portfolio is:
   $$\\text{Required Portfolio} = \\frac{\\$55,000}{0.04} = \\$1,375,000$$
3. If your projected retirement savings are only $900,000:
   $$\\text{Retirement Gap} = \\$1,375,000 - \\$900,000 = \\$475,000$$

This means you must save and invest enough to accumulate an additional **$475,000** to fund your desired lifestyle. Our tool helps you identify this gap clearly so you can adjust your plans.

### Actionable Methods to Successfully Close your Retirement Gap

* **Delay Your Social Security Claims**: Waiting to claim Social Security from age 62 to age 70 increases your monthly benefit by up to 8% per year, which significantly reduces your retirement gap.
* **Optimize Your Current Savings Rates**: Even small increases in your monthly savings can significantly accelerate your compound growth, helping you close your retirement gap faster.
* **Audit and Lower Your Retirement Expenses**: Standard housing, transport, and insurance obligations often decrease in retirement. Optimizing your retirement budget directly slashes your required portfolio size.`
    }
  },
  {
    id: "rmd-calculator",
    name: "RMD Calculator",
    description: "Estimate your IRS Required Minimum Distributions (RMD) from traditional IRS tax-deferred accounts based on your age.",
    category: "retirement",
    inputs: [
      { id: "priorYearBalance", label: "December 31st Portfolio Balance ($)", type: "currency", defaultValue: 400000, min: 1000, max: 10000000, step: 10000 },
      { id: "currentAge", label: "Age during Year", type: "number", defaultValue: 73, min: 72, max: 115, step: 1 }
    ],
    calculate: (inputs) => {
      const balance = inputs.priorYearBalance || 400000;
      const age = inputs.currentAge || 73;

      const getRMDFactor = (val: number) => {
        if (val < 72) return 0;
        if (val === 72) return 27.4;
        if (val === 73) return 26.5;
        if (val === 74) return 25.5;
        if (val === 75) return 24.6;
        if (val === 76) return 23.7;
        if (val === 77) return 22.9;
        if (val === 78) return 22.0;
        if (val === 79) return 21.1;
        if (val === 80) return 20.2;
        if (val === 81) return 19.4;
        if (val === 82) return 18.5;
        if (val === 83) return 17.7;
        if (val === 84) return 16.8;
        if (val === 85) return 16.0;
        if (val === 86) return 15.2;
        if (val === 87) return 14.4;
        if (val === 88) return 13.7;
        if (val === 89) return 12.9;
        if (val === 90) return 12.2;
        if (val === 91) return 11.5;
        if (val === 92) return 10.8;
        if (val === 93) return 10.1;
        if (val === 94) return 9.5;
        if (val === 95) return 8.9;
        if (val === 96) return 8.4;
        if (val === 97) return 7.8;
        if (val === 98) return 7.3;
        if (val === 99) return 6.8;
        if (val === 100) return 6.4;
        if (val >= 101 && val <= 104) return 5.5;
        if (val >= 105) return 4.0;
        return 26.5;
      };

      const factor = getRMDFactor(age);
      const rmd = factor > 0 ? balance / factor : 0;
      
      const penaltyDefault = rmd * 0.25;
      const penaltyCorrected = rmd * 0.10;

      const rec = factor === 0
        ? "Your age is below the typical SECURE Act 2.0 starting age (73) for RMD compliance, so you have zero mandatory IRS withdrawals this year."
        : `Your required minimum distribution is $${rmd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Be sure to withdraw this amount by December 31st to avoid IRS penalties.`;

      return {
        outputs: [
          { label: "Annual Required Minimum Payout (RMD)", value: rmd, type: "currency", highlighted: true },
          { label: "Monthly Distribution Outflow Equivalent", value: rmd / 12, type: "currency" },
          { label: "IRS Lifetime Expectancy factor (Divisor)", value: factor, type: "number", highlighted: true },
          { label: "RMD Percentage Allocation (%)", value: factor > 0 ? (1 / factor) * 100 : 0, type: "percentage" },
          { label: "IRS Default Noncompliance Penalty (25%)", value: penaltyDefault, type: "currency" },
          { label: "IRS Timely Corrected Reduced Penalty (10%)", value: penaltyCorrected, type: "currency" }
        ],
        chartData: [
          { name: "Dec 31 Balance", value: balance },
          { name: "Mandatory RMD Withdrawal", value: rmd }
        ],
        recommendation: rec
      };
    },
    guide: {
      overview: "Estimate your IRS Required Minimum Distributions (RMD) from traditional and employer-sponsored retirement plans based on your age.",
      howToUse: "Enter your overall traditional tax-deferred portfolio balance on December 31st of the prior year, and your current age.",
      formulaExplanation: "Lookup your age in the IRS Uniform Lifetime Table (Table III) to determine your life expectancy factor, and divide your prior year balance by this divisor: RMD = Dec 31 Balance / IRS Life Expectancy Factor.",
      faq: [
        { question: "What is an RMD?", answer: "Required Minimum Distributions are IRS-mandated withdrawals that you must take annually from tax-deferred retirement accounts (like traditional 401(k)s and IRAs) once you reach your target age. This ensures that you pay taxes on your tax-sheltered savings." },
        { question: "What is the RMD starting age under SECURE Act 2.0?", answer: "SECURE Act 2.0 increased the RMD starting age from 72 to 73 starting in 2023, and it will eventually increase to 75 in 2033." }
      ],
      seoContent800Words: `Planning for **Required Minimum Distributions (RMD)** is an essential slice of senior tax planning under the IRS tax code. To prevent savers from keeping tax-deferred funds in their accounts indefinitely, the IRS mandates annual withdrawals starting at age 73 (raising to 75 by 2033 under SECURE Act 2.0).

### The Mathematics Behind RMD Divisors and Tables

To calculate your annual RMD, you divide your total traditional (pre-tax) account balance on December 31st of the prior year by your IRS life expectancy factor (divisor) from the **IRS Uniform Lifetime Table (Table III)**.
The formula is:
$$\\text{Annual RMD} = \\frac{\\text{Prior Year's Dec 31 Balance}}{\\text{IRS Life Expectancy factor}}$$

Here is how the IRS divisors and percentage payouts adjust as you age:
* **Age 73**: Factor is **26.5**, requiring you to withdraw **3.77%** of your pre-tax assets.
* **Age 78**: Factor is **22.0**, requiring you to withdraw **4.55%** of your pre-tax assets.
* **Age 83**: Factor is **17.7**, requiring you to withdraw **5.65%** of your pre-tax assets.
* **Age 88**: Factor is **12.9**, requiring you to withdraw **7.75%** of your pre-tax assets.

For example, on a Traditional IRA balance of $400,000 at age 73:
$$\\text{RMD} = \\frac{\\$400,000}{26.5} = \\$15,094.34$$

You must withdraw at least **$15,094.34** before December 31st of the calendar year to avoid severe IRS penalties. This tool uses the exact SECURE Act tables to compute your required withdrawals.

### Actionable Tips to Optimize Your RMD Strategy

* **Execute Qualified Charitable Distributions (QCD)**: If you do not need your RMD funds to cover living expenses, you can direct up to $105,000 annually directly from your IRA to an eligible charity. This counts as your RMD while excluding the distribution from your taxable income entirely.
* **Consistently Leverage Roth Accounts**: Roth accounts (such as Roth IRAs, and Roth 401(k)s starting in 2024) are post-tax and are completely exempt from lifetime RMD mandates, allowing your assets to compound tax-free indefinitely.
* **Avoid Noncompliance Penalties**: Failure to withdraw your RMDs previously triggered a severe 50% IRS penalty. Under SECURE Act 2.0, this default penalty has been reduced to **25%** of the shortfall (and further lowered to **10%** if corrected in a timely manner).`
    }
  },
  {
    id: "retirement-nest-egg",
    name: "Retirement Nest Egg Calculator",
    description: "Project how large your retirement portfolio can grow based on starting capital, inflation, and monthly additions.",
    category: "retirement",
    inputs: [
      { id: "startingCapital", label: "Starting Assets Balance ($)", type: "currency", defaultValue: 45000, min: 0, max: 2000000, step: 5000 },
      { id: "yearsToGrow", label: "Horizon duration (Years)", type: "number", defaultValue: 30, min: 1, max: 50, step: 1 },
      { id: "monthlySavings", label: "Monthly Addition Savings ($)", type: "currency", defaultValue: 650, min: 0, max: 25000, step: 50 },
      { id: "expectedReturn", label: "Expect Portfolio Return (%)", type: "percentage", defaultValue: 8.0, min: 1, max: 20, step: 0.1 },
      { id: "inflationRate", label: "Expected Inflation Rate (%)", type: "percentage", defaultValue: 2.5, min: 0, max: 10, step: 0.1 }
    ],
    calculate: (inputs) => {
      const starting = inputs.startingCapital || 0;
      const years = inputs.yearsToGrow || 30;
      const addition = inputs.monthlySavings || 0;
      const retRate = inputs.expectedReturn || 8.0;
      const inflation = inputs.inflationRate || 2.5;

      const totalMonths = years * 12;
      const nominalMonthlyRate = retRate / 100 / 12;
      const realMonthlyRate = (retRate - inflation) / 100 / 12;

      let nominalBalance = starting;
      let realBalance = starting;
      let totalInvested = starting;

      for (let m = 1; m <= totalMonths; m++) {
        nominalBalance = (nominalBalance + addition) * (1 + nominalMonthlyRate);
        realBalance = (realBalance + addition) * (1 + realMonthlyRate);
        totalInvested += addition;
      }

      const postRetirementMonthlyNominal = (nominalBalance * 0.04) / 12;
      const postRetirementMonthlyReal = (realBalance * 0.04) / 12;

      const rec = `Projected Nest Egg size is $${nominalBalance.toLocaleString("en-US", { maximumFractionDigits: 0 })}. When adjusted for inflation, your portfolio represents $${realBalance.toLocaleString("en-US", { maximumFractionDigits: 0 })} in today's purchasing power, which can safely produce $${postRetirementMonthlyReal.toLocaleString("en-US", { maximumFractionDigits: 0 })} in monthly real income.`;

      return {
        outputs: [
          { label: "Nominal End Balance", value: nominalBalance, type: "currency", highlighted: true },
          { label: "Real Nest Egg (Purchasing Power)", value: realBalance, type: "currency", highlighted: true },
          { label: "Total Out-of-Pocket Deposits", value: totalInvested, type: "currency" },
          { label: "Safe Monthly Drawdown (Nominal)", value: postRetirementMonthlyNominal, type: "currency" },
          { label: "Safe Monthly Drawdown (Real Value)", value: postRetirementMonthlyReal, type: "currency" }
        ],
        chartData: [
          { name: "My Contributions", value: totalInvested },
          { name: "Portfolio Real Gains", value: Math.max(0, realBalance - totalInvested) },
          { name: "Inflation Impact", value: Math.max(0, nominalBalance - realBalance) }
        ],
        recommendation: rec
      };
    },
    guide: {
      overview: "Project the size of your retirement nest egg and your inflation-adjusted purchasing power, based on starting capital and monthly additions.",
      howToUse: "Enter your initial portfolio, target horizon, ongoing monthly additions, projected stock market return, and estimated inflation.",
      formulaExplanation: "Compounds your assets monthly under two distinct rate horizons: expected nominal return rate and expected real return rate (nominal return rate minus inflation). This reveals your future nominal portfolio size alongside its real purchasing power in today's dollars.",
      faq: [
        { question: "What is nominal vs. real nest egg?", answer: "Nominal value represents the future cash balance in that calendar year, whereas real value adjusts this balance for inflation to show its actual purchasing power in today's dollars. This keeps your retirement planning accurate." },
        { question: "How does inflation affect my investments over 30 years?", answer: "Over long horizons, inflation has a major compounding effect. At an average inflation rate of 2.5%, the purchasing power of $1 million is reduced to roughly $476,000 in 30 years." }
      ],
      seoContent800Words: `Planning a successful retirement requires building a reliable, long-term portfolio without relying on simple guesses. The **Retirement Nest Egg Calculator** projects the size of your portfolio and your inflation-adjusted purchasing power, based on starting capital and monthly additions.

### The Mathematics Behind Nest Egg Projections

Your ultimate portfolio size is driven by your starting capital, consistent monthly additions, expected asset returns, and your investment horizon:
$$FV_{\\text{nominal}} = PV(1+r)^n + M_{\\text{monthly}} \\cdot \\frac{(1+r)^n - 1}{r}$$
Where:
* **$PV$** is your starting capital balance today.
* **$M_{\\text{monthly}}$** is your ongoing monthly addition.
* **$r$** is the nominal monthly growth rate (Annual rate / 12 / 100).
* **$n$** is the compounding horizon in months (Years multiplied by 12).

For example, starting with $45,000 at age 30 and depositing $650 a month with an estimated 8.0% annual growth rate over 30 years:
1. Your total investment horizon is **360 Months**.
2. Your portfolio will grow to an estimated nominal balance of **$1,363,205.51**.
3. However, adjusting for an annual inflation rate of 2.5%, your expected real return rate is **5.5%**, resulting in a real nest egg of:
   $$FV_{\\text{real}} = \\$760,098.41$$

While your account will show $1,363,205.51 in cash, its actual purchasing power in today's dollars is equivalent to **$760,098.41**. This real nest egg can safely produce an annual income of **$30,403.94** under a standard 4.0% SWR, which translates to a monthly payout of **$2,533.66**.

### Actionable Methods to Optimize your Retirement Nest Egg

* **Consistently Increase Your Deposits**: As your income grows, increase your monthly contributions. Automating minor annual increases (such as 1% more each year) has a major compounding effect over time.
* **Capitalize on Plan Matches**: Secure your full employer matching contributions in Traditional or Roth 401(k) accounts. This provides instantly doubled, tax-sheltered capital.
* **Minimize Fund Expense Ratios**: Choose passive index fund ETFs with low expense ratios (ideally below 0.15%) to avoid management fees that eat away your gains.`
    }
  }
];
